import type { ECharts, EChartsOption, LegendComponentOption } from 'echarts';

export const USE_PUBLICATION_CHART_EXPORT = true;

const EXPORT_BACKGROUND = '#ffffff';
const EXPORT_MIN_WIDTH = 1100;
const EXPORT_PLOT_HEIGHT = 420;
const PAD_X = 16;
const PAD_TOP = 8;
const TITLE_FONT = 15;
const TITLE_LINE = 22;
const FOOTER_FONT = 11;
const FOOTER_LINE = 16;
const LEGEND_ROW = 28;
const LEGEND_ITEM_GAP = 16;
const AXIS_NAME_GAP = 28;
/** Espaço extra sob o mapa para a escala do visualMap. */
const MAP_SCALE_GAP = 108;

const INSET_SERIES = new Set(['pie', 'sankey', 'treemap', 'sunburst', 'funnel', 'graph', 'tree', 'gauge']);

/**
 * Contexto que a tela já mostra fora do gráfico e da grade.
 * A imagem usa título, fonte, notas e atualização.
 * A planilha, quando `USE_PUBLICATION_TABLE_EXPORT` está ligado, usa também
 * unidade, território, período e desagregações.
 */
export interface ChartPublicationCaption {
  title?: string;
  unit?: string;
  territory?: string;
  period?: string;
  disaggregations?: string;
  source?: string;
  notes?: string;
  updatedAt?: string;
  sourceLabel?: string;
  updatedAtLabel?: string;
  /** Rótulos da ficha da planilha. Ausentes, usam o padrão em pt-BR. */
  fieldLabels?: {
    title?: string;
    unit?: string;
    territory?: string;
    period?: string;
    disaggregations?: string;
    notes?: string;
    country?: string;
    codes?: string;
    /** Frase do código ausente. `{code}` é trocado por `N/A` ou `NA`. */
    missingValue?: string;
  };
}

export interface PublicationChartLayout {
  option: EChartsOption;
  width: number;
  height: number;
}

function downloadHref(href: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Exporta o gráfico já renderizado como PNG. `pixelRatio: 2` mantém o texto legível
 * quando a imagem é ampliada ou impressa.
 */
export function downloadChartPng(chart: ECharts, fileName: string): boolean {
  if (typeof document === 'undefined') return false;
  const href = chart.getDataURL({
    type: 'png',
    pixelRatio: 2,
    backgroundColor: EXPORT_BACKGROUND,
    excludeComponents: ['toolbox', 'dataZoom'],
  });
  downloadHref(href, withExtension(fileName, 'png'));
  return true;
}

/**
 * Gera o SVG a partir de uma instância temporária fora da tela com o renderer `svg`.
 *
 * A instância visível usa canvas (melhor desempenho com mapas e séries grandes) e o
 * ECharts só expõe `renderToSVGString` no renderer SVG — por isso o gráfico é
 * re-renderizado num container descartável em vez de trocar o renderer da tela.
 */
export async function chartToSvgString(
  option: EChartsOption,
  width: number,
  height: number,
): Promise<string | null> {
  return withOffscreenChart(option, width, height, 'svg', (chart) => chart.renderToSVGString());
}

export async function downloadChartSvg(
  option: EChartsOption,
  fileName: string,
  width: number,
  height: number,
): Promise<boolean> {
  const svg = await chartToSvgString(option, width, height);
  if (!svg) return false;

  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  downloadHref(url, withExtension(fileName, 'svg'));
  URL.revokeObjectURL(url);
  return true;
}

/** PNG da figura montada fora da tela — não altera o gráfico visível. */
export async function downloadChartPngFromOption(
  option: EChartsOption,
  fileName: string,
  width: number,
  height: number,
): Promise<boolean> {
  const href = await withOffscreenChart(option, width, height, 'canvas', (chart) =>
    chart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: EXPORT_BACKGROUND,
      excludeComponents: ['toolbox', 'dataZoom'],
    }),
  );
  if (!href) return false;
  downloadHref(href, withExtension(fileName, 'png'));
  return true;
}

/**
 * Monta a opção e o tamanho da figura de exportação.
 * A tela continua com legenda paginada; só o arquivo traz todas as categorias,
 * o título e o rodapé de fonte/notas.
 */
export function buildPublicationChart(
  option: EChartsOption,
  size: { width: number; height: number },
  caption: ChartPublicationCaption = {},
): PublicationChartLayout {
  const width = Math.max(EXPORT_MIN_WIDTH, Math.round(size.width) || EXPORT_MIN_WIDTH);
  const contentWidth = width - PAD_X * 2;
  const title = caption.title?.trim() || visibleOptionTitle(option);
  const footer = buildFooter(caption);
  const titleLines = title ? estimateWrappedLines(title, charsPerLine(contentWidth, TITLE_FONT)) : 0;
  const footerLines = footer ? estimateWrappedLines(footer, charsPerLine(contentWidth, FOOTER_FONT)) : 0;
  const titleBlock = titleLines ? PAD_TOP + titleLines * TITLE_LINE + 12 : PAD_TOP;
  const footerBlock = footerLines ? 12 + footerLines * FOOTER_LINE + 8 : 0;

  const legendInfo = readLegend(option);
  const rows = legendInfo.names.length ? packLegendRows(legendInfo.names, contentWidth) : [];
  const legendBlock = rows.length ? rows.length * LEGEND_ROW + 4 : 0;
  const plotHeight = Math.max(EXPORT_PLOT_HEIGHT, Math.round(size.height) || EXPORT_PLOT_HEIGHT);
  const height = titleBlock + plotHeight + legendBlock + footerBlock;
  const belowPlot = legendBlock + footerBlock;

  const next: EChartsOption = { ...option, backgroundColor: EXPORT_BACKGROUND };
  next.title = title
    ? {
        show: true,
        text: title,
        top: PAD_TOP,
        left: PAD_X,
        textStyle: {
          width: contentWidth,
          overflow: 'break',
          fontSize: TITLE_FONT,
          fontWeight: 600,
          lineHeight: TITLE_LINE,
          color: '#111827',
        },
      }
    : { show: false };

  if (rows.length) {
    next.legend = rows.map((data, index) => {
      const item: LegendComponentOption = {
        show: true,
        type: 'plain',
        orient: 'horizontal',
        left: 'center',
        bottom: footerBlock + (rows.length - 1 - index) * LEGEND_ROW,
        data,
        itemWidth: 14,
        itemHeight: 10,
        itemGap: LEGEND_ITEM_GAP,
        padding: [4, 8],
        textStyle: { fontSize: 12, color: '#374151' },
      };
      if (legendInfo.selected) item.selected = legendInfo.selected;
      if (legendInfo.icon) item.icon = legendInfo.icon;
      return item;
    });
  }

  const kind = detectKind(option);
  if (kind === 'cartesian') {
    next.grid = patchGrids(option.grid, titleBlock + AXIS_NAME_GAP, belowPlot);
  } else if (kind === 'map') {
    next.series = insetSeries(option.series, titleBlock, belowPlot + MAP_SCALE_GAP, true);
    if (option.visualMap) {
      next.visualMap = placeVisualMap(option.visualMap, footerBlock + legendBlock + 8);
    }
  } else if (kind === 'inset') {
    next.series = insetSeries(option.series, titleBlock, belowPlot, false);
  }

  if (footer) {
    const note = {
      type: 'text' as const,
      left: PAD_X,
      bottom: 8,
      silent: true,
      style: {
        text: footer,
        fill: '#4b5563',
        fontSize: FOOTER_FONT,
        lineHeight: FOOTER_LINE,
        width: contentWidth,
        overflow: 'break' as const,
      },
    };
    const existing = option.graphic;
    const previous = Array.isArray(existing) ? existing : existing ? [existing] : [];
    next.graphic = [...previous, note];
  }

  return { option: next, width, height };
}

function buildFooter(caption: ChartPublicationCaption): string {
  const lines: string[] = [];
  const source = caption.source?.trim();
  if (source) lines.push(`${caption.sourceLabel?.trim() || 'Fonte'}: ${source}`);
  const notes = caption.notes?.trim();
  if (notes) lines.push(notes);
  const updatedAt = caption.updatedAt?.trim();
  if (updatedAt) lines.push(`${caption.updatedAtLabel?.trim() || 'Última atualização'}: ${updatedAt}`);
  return lines.join('\n');
}

function visibleOptionTitle(option: EChartsOption): string {
  return asArray(option.title)
    .filter((item) => item.show !== false && !!item.text?.trim())
    .map((item) => item.text!.trim())
    .join('\n');
}

interface LegendRead {
  names: string[];
  selected?: Record<string, boolean>;
  icon?: LegendComponentOption['icon'];
}

function readLegend(option: EChartsOption): LegendRead {
  const legends = asArray(option.legend).filter((item) => item.show !== false);
  if (!legends.length) return { names: [] };

  const names: string[] = [];
  let selected: Record<string, boolean> | undefined;
  const icon = legends[0]?.icon;

  for (const legend of legends) {
    if (legend.selected) selected = { ...selected, ...legend.selected };
    for (const entry of legend.data ?? []) {
      const name = entryName(entry);
      if (name) names.push(name);
    }
  }

  if (!names.length) names.push(...seriesLegendNames(option));
  return { names: unique(names), selected, icon };
}

function seriesLegendNames(option: EChartsOption): string[] {
  const names: string[] = [];
  for (const series of asArray(option.series)) {
    const item = series as { name?: unknown; type?: string; data?: unknown };
    if (item.type === 'pie' && Array.isArray(item.data)) {
      for (const point of item.data) {
        const name = entryName(point);
        if (name) names.push(name);
      }
      continue;
    }
    if (typeof item.name === 'string' && item.name) names.push(item.name);
  }
  return names;
}

function entryName(entry: unknown): string {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object' && 'name' in entry && (entry as { name: unknown }).name != null) {
    return String((entry as { name: unknown }).name);
  }
  return '';
}

function unique(names: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of names) {
    if (seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result;
}

function packLegendRows(names: string[], maxWidth: number): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let used = 0;
  for (const name of names) {
    const item = 22 + Math.ceil(name.length * 6.6);
    const gap = row.length ? LEGEND_ITEM_GAP : 0;
    if (row.length && used + gap + item > maxWidth) {
      rows.push(row);
      row = [name];
      used = item;
    } else {
      row.push(name);
      used += gap + item;
    }
  }
  if (row.length) rows.push(row);
  return rows;
}

type ChartKind = 'cartesian' | 'map' | 'inset' | 'plain';

function detectKind(option: EChartsOption): ChartKind {
  const types = asArray(option.series).map((series) => String((series as { type?: string }).type ?? ''));
  if (types.includes('map')) return 'map';
  if (option.xAxis || option.yAxis || option.grid) return 'cartesian';
  if (types.some((type) => INSET_SERIES.has(type))) return 'inset';
  return 'plain';
}

function patchGrids(grid: EChartsOption['grid'], top: number, bottom: number): EChartsOption['grid'] {
  const apply = (item: object | undefined) => ({ ...(item ?? {}), top, bottom, containLabel: true });
  if (Array.isArray(grid)) return grid.map((item) => apply(item as object));
  return apply(grid as object | undefined);
}

function insetSeries(
  series: EChartsOption['series'],
  top: number,
  bottom: number,
  map: boolean,
): EChartsOption['series'] {
  const apply = (item: object) => {
    const type = (item as { type?: string }).type ?? '';
    if (map ? type !== 'map' : !INSET_SERIES.has(type)) return item;
    return { ...item, top, bottom };
  };
  if (Array.isArray(series)) return series.map((item) => apply(item as object)) as EChartsOption['series'];
  if (series && typeof series === 'object') return apply(series) as EChartsOption['series'];
  return series;
}

function placeVisualMap(visualMap: EChartsOption['visualMap'], bottom: number): EChartsOption['visualMap'] {
  const place = (item: object) => {
    const next = { ...(item as Record<string, unknown>) };
    delete next['top'];
    next['bottom'] = bottom;
    return next;
  };
  if (Array.isArray(visualMap)) return visualMap.map((item) => place(item as object)) as EChartsOption['visualMap'];
  if (visualMap && typeof visualMap === 'object') return place(visualMap) as EChartsOption['visualMap'];
  return visualMap;
}

function charsPerLine(widthPx: number, fontSize: number): number {
  return Math.max(20, Math.floor(widthPx / (fontSize * 0.5)));
}

function estimateWrappedLines(text: string, maxChars: number): number {
  let lines = 0;
  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines += 1;
      continue;
    }
    let length = 0;
    let row = 1;
    for (const word of words) {
      const chunks = Math.max(1, Math.ceil(word.length / maxChars));
      if (chunks > 1) {
        if (length > 0) row += 1;
        row += chunks - 1;
        length = word.length % maxChars || maxChars;
        continue;
      }
      const extra = (length === 0 ? 0 : 1) + word.length;
      if (length > 0 && length + extra > maxChars) {
        row += 1;
        length = word.length;
      } else {
        length += extra;
      }
    }
    lines += row;
  }
  return Math.max(lines, 1);
}

function asArray<T>(value: T | readonly T[] | undefined | null): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? [...value] : [value as T];
}

async function withOffscreenChart<T>(
  option: EChartsOption,
  width: number,
  height: number,
  renderer: 'canvas' | 'svg',
  read: (chart: ECharts) => T,
): Promise<T | null> {
  if (typeof document === 'undefined') return null;

  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;height:${height}px;pointer-events:none`;
  document.body.appendChild(host);

  let instance: ECharts | null = null;
  try {
    const echarts = await import('echarts');
    instance = echarts.init(host, undefined, { renderer, width, height });
    instance.setOption({ ...option, animation: false, backgroundColor: EXPORT_BACKGROUND }, true);
    instance.resize({ width, height });
    return read(instance);
  } catch (error) {
    console.error('Falha ao gerar imagem do gráfico', error);
    return null;
  } finally {
    instance?.dispose();
    host.remove();
  }
}

function withExtension(fileName: string, extension: string): string {
  const base = fileName || 'grafico';
  return base.toLowerCase().endsWith(`.${extension}`) ? base : `${base}.${extension}`;
}
